import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import ImageCropModal from '../../common/ImageCropModal';
import type { ReviewConfig } from '../checkout-components/CheckoutReview';

interface ReviewInspectorProps {
  config: ReviewConfig;
  onConfigChange: (patch: Partial<ReviewConfig>) => void;
}

const MAX_SIZE_MB = 5;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const inputCls =
  'w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500';
const labelCls = 'block text-sm text-gray-700 dark:text-gray-300 mb-1';

/**
 * Painel de edição do componente "Avaliação" (modelo Hotmart):
 * Nome, Avaliação, Estrelas, Imagem (avatar) e Horizontal.
 * A imagem fica em base64 até salvar; o BuilderShell envia para o S3 no save.
 */
const ReviewInspector: React.FC<ReviewInspectorProps> = ({ config, onConfigChange }) => {
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  const handleFile = (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Envie uma imagem PNG, JPG ou WEBP');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Tamanho máximo de ${MAX_SIZE_MB}MB`);
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => setCropSrc(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const avatarUrl = config.avatarUrl || '';

  return (
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Nome</label>
        <input
          type="text"
          className={inputCls}
          value={config.name || ''}
          maxLength={80}
          onChange={(e) => onConfigChange({ name: e.target.value })}
        />
      </div>

      <div>
        <label className={labelCls}>Avaliação</label>
        <textarea
          className={`${inputCls} min-h-[84px] resize-y`}
          value={config.text || ''}
          maxLength={600}
          onChange={(e) => onConfigChange({ text: e.target.value })}
        />
      </div>

      <div>
        <label className={labelCls}>Estrelas</label>
        <select
          className={inputCls}
          value={String(config.stars || 5)}
          onChange={(e) => onConfigChange({ stars: parseInt(e.target.value, 10) })}
        >
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>
              {n} {n === 1 ? 'estrela' : 'estrelas'}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelCls}>Imagem</label>
        {avatarUrl ? (
          <div className="flex items-center gap-3 rounded-md border border-gray-200 dark:border-gray-700 p-3">
            <img src={avatarUrl} alt="Avatar" className="w-16 h-16 rounded-full object-cover" />
            <div className="flex flex-col gap-1.5 text-sm">
              <label className="text-blue-600 font-medium cursor-pointer hover:underline">
                Trocar imagem
                <input
                  type="file"
                  accept={ACCEPTED_TYPES.join(',')}
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
                />
              </label>
              <button
                type="button"
                className="text-left text-red-600 font-medium hover:underline"
                onClick={() => onConfigChange({ avatarUrl: '' })}
              >
                Remover imagem
              </button>
            </div>
          </div>
        ) : (
          <label
            className={`mx-auto flex flex-col items-center justify-center w-full max-w-[220px] aspect-square rounded-lg border border-dashed cursor-pointer transition-colors ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-blue-400 hover:bg-blue-50/50'}`}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
          >
            <i className="ri-image-fill text-4xl text-blue-600"></i>
            <span className="mt-2 px-4 text-center text-sm font-medium text-blue-600">
              Clique ou arraste uma imagem para enviar
            </span>
            <input
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
            />
          </label>
        )}
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none">
        <input
          type="checkbox"
          className="w-4 h-4 rounded border-gray-300"
          checked={!!config.horizontal}
          onChange={(e) => onConfigChange({ horizontal: e.target.checked })}
        />
        Horizontal
      </label>

      {cropSrc && createPortal(
        <ImageCropModal
          imageSrc={cropSrc}
          open={!!cropSrc}
          aspectRatio={1}
          maxOutputSize={400}
          onClose={() => setCropSrc(null)}
          onCropComplete={(cropped: string) => {
            onConfigChange({ avatarUrl: cropped });
            setCropSrc(null);
          }}
        />,
        window.document.body
      )}
    </div>
  );
};

export default ReviewInspector;
